import { Question } from '../../../../datasources/questions/QuestionsDataSource';
import {
  FlowDefinition,
  FlowNode,
  FlowType,
} from '../../../../types/flow/Flow';

export const FLOW_TYPES: { value: FlowType; label: string }[] = [
  { value: 'MAIN', label: 'Principal' },
  { value: 'ALTERNATIVE', label: 'Alternativo' },
  { value: 'CUSTOM', label: 'Personalizado' },
];

export function createNodeId(questionCode: string, nodes: FlowNode[]): string {
  const baseId = `q_${questionCode.toLowerCase().replace(/[^a-z0-9_-]/g, '_')}`;
  let candidate = baseId;
  let suffix = 2;
  while (nodes.some((node) => node.id === candidate)) {
    candidate = `${baseId}_${suffix}`;
    suffix += 1;
  }
  return candidate;
}

export function validateDefinition(
  definition: FlowDefinition,
  questionsByCode: Map<string, Question>
): string | undefined {
  if (definition.nodes.length === 0) {
    return 'Adicione pelo menos uma pergunta para começar.';
  }

  const nodeIds = new Set(definition.nodes.map((node) => node.id));
  const nodeQuestions = new Set(definition.nodes.map((node) => node.question));
  if (nodeIds.size !== definition.nodes.length) {
    return 'Cada pergunta pode aparecer apenas uma vez no fluxo.';
  }
  const questionCounts = new Map<string, number>();
  definition.nodes.forEach((node) => {
    questionCounts.set(node.question, (questionCounts.get(node.question) ?? 0) + 1);
  });
  let repeatedQuestionNotAllowed = false;
  questionCounts.forEach((count, code) => {
    if (count > 1 && !questionsByCode.get(code)?.is_loop) {
      repeatedQuestionNotAllowed = true;
    }
  });
  if (repeatedQuestionNotAllowed) {
    return 'Uma mesma pergunta só pode ser adicionada mais de uma vez se permitir repetição.';
  }

  const incoming = new Map<string, number>();
  const adjacency = new Map<string, string[]>();
  definition.nodes.forEach((node) => {
    incoming.set(node.id, 0);
    adjacency.set(node.id, []);
  });

  for (const edge of definition.edges) {
    if (!nodeIds.has(edge.from) || !nodeIds.has(edge.to)) {
      return 'Revise os caminhos: eles devem ligar perguntas adicionadas ao fluxo.';
    }
    if (edge.from === edge.to) {
      return 'Uma pergunta não pode levar de volta para ela mesma.';
    }
    incoming.set(edge.to, (incoming.get(edge.to) ?? 0) + 1);
    adjacency.get(edge.from)?.push(edge.to);

    const conditions = [...(edge.when?.all ?? []), ...(edge.when?.any ?? [])];
    if (
      conditions.some((condition) => !nodeQuestions.has(condition.question))
    ) {
      return 'As regras devem usar perguntas que fazem parte deste fluxo.';
    }
    if (
      conditions.some(
        (condition) =>
          condition.value === '' ||
          (Array.isArray(condition.value) && condition.value.length === 0)
      )
    ) {
      return 'Informe uma resposta para cada regra ou remova a regra incompleta.';
    }
  }

  const roots = definition.nodes
    .filter((node) => incoming.get(node.id) === 0)
    .map((node) => node.id);
  if (roots.length !== 1) {
    return 'Defina uma única pergunta para iniciar o fluxo.';
  }

  const visited = new Set<string>();
  const active = new Set<string>();
  function visit(nodeId: string): boolean {
    if (active.has(nodeId)) {
      return false;
    }
    if (visited.has(nodeId)) {
      return true;
    }
    active.add(nodeId);
    for (const destination of adjacency.get(nodeId) ?? []) {
      if (!visit(destination)) {
        return false;
      }
    }
    active.delete(nodeId);
    visited.add(nodeId);
    return true;
  }

  if (!visit(roots[0])) {
    return 'Revise os caminhos: uma pergunta não pode levar de volta a uma etapa anterior.';
  }
  if (visited.size !== definition.nodes.length) {
    return 'Todas as perguntas precisam ter um caminho a partir da pergunta inicial.';
  }

  return undefined;
}
